<template>
  <WdsStack gap-sm direction-column>
    <WdsSelect
      v-model="selectedIdentifiers"
      multi-select
      searchable
      :disabled
      :title
      :label="$t('Workers External Identifier')"
      :list="externalIdentifiers"
      :placeholder="$t('External identifier')"
      can-deselect
      max-labels-visible="5"
    />
    <WdsCheckbox :checked="allChecked" :disabled @update:checked="toggleIdentifiers">
      {{ $t('Select all') }}
    </WdsCheckbox>
  </WdsStack>
</template>

<script setup lang="ts">
import { WdsCheckbox, WdsSelect, WdsStack } from '@worksome/wds-components'
import { computed, ComputedRef, ModelRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { ListItem } from '@/components/Workflows/Pages/Workflow/workflow'
import { useAccountsStore } from '@/components/GraphqlDataTable/filters/accounts/accounts-store'
import { graphql } from '@/gql'
import { $t } from '@/composables/translation'

const selectedIdentifiers: ModelRef<ListItem[]> = defineModel({
  default: () => [],
})

const { selectedAccounts } = useAccountsStore()

const { result } = useQuery(
  graphql(`
    query ExternalIdentifiers($accounts: [ID!]) {
      hires(accounts: $accounts, first: 100000) {
        data {
          label: externalIdentifier
          value: externalIdentifier
        }
      }
    }
  `),
  () => ({
    accounts: selectedAccounts.map((account) => account.id),
  }),
  {
    clientId: 'publicApiCustomFields',
  },
)

const externalIdentifiers: ComputedRef<ListItem[]> = computed(() => {
  const set = new Set(result.value?.hires.data)
  return [...set]
    .filter(
      ({ value, label }) =>
        value !== null && value !== undefined && label !== null && label !== undefined,
    )
    .map((identifiers) => ({
      value: identifiers.value,
      label: String(identifiers.label),
    }))
})

const disabled = computed(() => externalIdentifiers.value.length === 0)

const title = computed(() =>
  disabled.value ? $t('No external identifiers found in data') : $t('External identifier'),
)

const allChecked = computed(
  () =>
    selectedIdentifiers.value?.length === externalIdentifiers.value.length &&
    !!selectedIdentifiers.value?.length,
)

const toggleIdentifiers = (event: boolean) => {
  // If 'event' is true, select all accounts; otherwise, deselect all
  selectedIdentifiers.value = event ? externalIdentifiers.value : []
}
</script>
